import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../../utils/AuthContext';
import { assertCsvResponse, buildApiUrl, getApiBaseUrl } from '../../services/apiBase';

const ChatInterface = ({ onMessageSent }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [conversationId, setConversationId] = useState(null);
  const messagesEndRef = useRef(null);
  const { user, token } = useAuth();
  const socketRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const intentionalCloseRef = useRef(false);
  const reconnectAttemptsRef = useRef(0);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const connectWebSocket = useCallback(() => {
    if (!token || (socketRef.current && socketRef.current.readyState === WebSocket.OPEN)) {
      return;
    }

    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }

    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const apiBaseUrl = getApiBaseUrl();
    const parsedApiUrl = new URL(apiBaseUrl, window.location.origin);
    const wsHost = parsedApiUrl.host;
    const basePath = parsedApiUrl.pathname && parsedApiUrl.pathname !== '/'
      ? parsedApiUrl.pathname.replace(/\/$/, '')
      : '';
    const wsUrl = `${wsProtocol}//${wsHost}${basePath}/chat/ws/chat?token=${token}`;
    
    socketRef.current = new WebSocket(wsUrl);

    socketRef.current.onopen = () => {
      console.log('WebSocket connected');
      reconnectAttemptsRef.current = 0;
    };

    socketRef.current.onmessage = (event) => {
      const message = JSON.parse(event.data);

      if (message.status === 'progress') {
        setLoadingMessage(message.message);
        setLoadingProgress(message.progress);
      } else if (message.status === 'final') {
        const responseData = message.data;
        if (!conversationId) {
          setConversationId(responseData.conversation_id);
        }
        const aiMessage = {
          role: 'assistant',
          content: responseData.response,
          timestamp: responseData.timestamp,
          csvAvailable: responseData.csv_available || false,
        };
        setMessages((prev) => [...prev, aiMessage]);
        setLoading(false);
        if (onMessageSent) {
          onMessageSent();
        }
      } else if (message.status === 'error') {
        const errorMessage = {
          role: 'assistant',
          content: message.message || 'An unknown error occurred.',
          timestamp: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, errorMessage]);
        setLoading(false);
      }
    };

    socketRef.current.onclose = (event) => {
      const closeCode = event?.code;
      if (intentionalCloseRef.current) {
        return;
      }

      // Policy violation means invalid/expired token; avoid infinite reconnect loops.
      if (closeCode === 1008) {
        setLoading(false);
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: 'Your session expired. Please refresh or log in again.',
            timestamp: new Date().toISOString(),
          },
        ]);
        return;
      }

      reconnectAttemptsRef.current += 1;
      if (reconnectAttemptsRef.current > 3) {
        setLoading(false);
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: 'Chat connection failed repeatedly. Please refresh the page to re-authenticate.',
            timestamp: new Date().toISOString(),
          },
        ]);
        return;
      }

      reconnectTimerRef.current = setTimeout(() => {
        connectWebSocket();
      }, 3000);
    };

    socketRef.current.onerror = (error) => {
      console.error('WebSocket error:', error);
      // The onclose event will usually fire after an error.
    };

  }, [token, conversationId, onMessageSent]);

  useEffect(() => {
    intentionalCloseRef.current = false;
    if (token) {
      connectWebSocket();
    }

    return () => {
      intentionalCloseRef.current = true;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      reconnectAttemptsRef.current = 0;
      if (socketRef.current) {
        socketRef.current.close();
      }
    };
  }, [token, connectWebSocket]);


  const downloadCSV = async () => {
    try {
      const apiUrl = conversationId
        ? `${buildApiUrl('/export/csv')}?conversation_id=${encodeURIComponent(conversationId)}`
        : buildApiUrl('/export/csv');
      
      const response = await fetch(apiUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to download CSV');
      assertCsvResponse(response);
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `sites_export_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading CSV:', error);
      alert('Failed to download CSV. Please try again.');
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Welcome message
    if (user && messages.length === 0) {
      setMessages([
        {
          role: 'assistant',
          content: `Welcome back, ${user.full_name}! I'm here to help you with site intelligence. How can I assist you today?`,
          timestamp: new Date().toISOString(),
        },
      ]);
    }
  }, [user, messages.length]);

  const handleSend = async () => {
    if (!input.trim() || loading || !socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) return;

    const userMessage = {
      role: 'user',
      content: input,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    socketRef.current.send(JSON.stringify({
      message: input,
      conversation_id: conversationId,
    }));
    setInput('');
    setLoading(true);
    setLoadingMessage('Request initiated...');
    setLoadingProgress(10);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-lg shadow">
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-3xl px-4 py-2 rounded-lg ${
                msg.role === 'user'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-900'
              }`}
            >
              <div className="whitespace-pre-wrap">{msg.content}</div>
              {msg.csvAvailable && msg.role === 'assistant' && (
                <button
                  onClick={downloadCSV}
                  className="mt-2 inline-flex items-center px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition"
                >
                  📥 Download as CSV
                </button>
              )}
              <div
                className={`text-xs mt-1 ${
                  msg.role === 'user' ? 'text-blue-100' : 'text-gray-500'
                }`}
              >
                {new Date(msg.timestamp).toLocaleTimeString()}
              </div>
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 px-4 py-2 rounded-lg">
                <div className="w-72">
                  <div className="text-xs text-gray-600 mb-2">{loadingMessage}</div>
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 transition-all duration-500 ease-linear"
                      style={{ width: `${loadingProgress}%` }}
                    />
                  </div>
                </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="border-t p-4">
        <div className="flex space-x-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            rows="2"
          />
          <button
            onClick={handleSend}
            disabled={loading || !input.trim()}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;
